import React, { useState } from 'react'
import PropTypes from 'prop-types'
import noop from 'lodash/noop'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { toast } from 'react-toastify'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom-v5-compat'

import { Menu, MenuItem, ListItemIcon, ListItemText, Tooltip, SvgIcon } from '@mui/material'
import { ReactComponent as WorkOrderNoteIcon } from 'assets/note/WorkOrderNote.svg'
import { ReactComponent as DriverPath } from 'assets/route_manager/actions_menu/DriverPath.svg'
import { ReactComponent as AssignStop } from 'assets/route_manager/actions_menu/AssignStop.svg'

import { Printer } from '@styled-icons/heroicons-outline/Printer'
import { CheckCircle } from '@styled-icons/heroicons-outline/CheckCircle'
import { ReactComponent as Swap } from 'assets/services/actions/Swap.svg'

import { get } from 'utils/lodash'
import {
  useCreateWorkOrderNoteMutation,
  useUpdateWorkOrderNoteNewMutation,
  usePatchWorkOrderNoteMutation,
  useLazyGetAllWorkOrderNotesQuery,
} from 'api/notes/workOrderNotesCrud'
import { getSortedNotes, getArchivedAndUnarchivedNotes } from 'data/notes/notesSelectors'
import { INVOICE_STATUS, INVOICE_STATUS_DISPLAY } from 'settings/constants/invoice'
import { useUpdateWorkOrderMutation } from 'api/work-order/updateWorkOrder'
import { handleError } from 'utils/error'
import { canChangeWOInvoiceStatusSelector, selectIsOneOff } from 'data/work-order/workOrderSelectors'
import { APP_PREFIX } from 'router/routes'
import { isWORedesignFeatureAvailable } from 'data/launch-darkly/featureSelectors'
import { getWorkOrderListInvalidatesTags } from 'components/customer-details/content/work-orders/utils'

import T from 'T'
import api from 'api'
import Loader from 'components/common/loader'
import ViewArchiveNotesDialog from 'components/notes/common/ViewArchiveNotesDialog'
import ViewArchivedNotesMenuOption from 'components/notes/common/actions/ViewArchivedNotesMenuOption'
import AddUpdateNoteDialog from 'components/notes/common/AddUpdateNoteDialog'

import CommonSwitch from 'components/common/CommonSwitch'
import ExternalLinkBehavior from 'components/common/ExternalLinkBehavior'
import AddEditPurchaseOrderMenuOption from 'components/work-order/purchase-order/AddEditPurchaseOrderMenuOption'
import RemovePurchaseOrderMenuOption from 'components/work-order/purchase-order/RemovePurchaseOrderMenuOption'
import EditActionDialog from 'components/work-order/details/action-menu/EditActionDialog/EditActionDialog'
import {
  setIsAssignStopDialogOpen,
  setStopToBeAssigned,
  setIsUnassignConfirmDialogOpen,
  setUnassignConfirmDialogAccountId,
  setRoutingActionServiceDate,
} from 'slices/route/routingActionsSlice'
import { combineAddressInfo } from 'utils/helper'
import { createDateFromString } from 'utils/date'

const { NOT_READY_FOR_INVOICE, READY_FOR_INVOICE } = INVOICE_STATUS

const WorkOrderActionMenu = ({ anchorEl, workOrder = {}, onClose, onSuccess = noop, enableRoutingActions = false }) => {
  const dispatch = useDispatch()
  const { accountId } = useParams()
  const customerAccountId = useSelector(s => get(s, 'CustomersReducer.customerDetails.id', ''))
  const effectiveAccountId = accountId || customerAccountId
  const [updateWorkOrder, { isLoading: isUpdateWorkOrderLoading }] = useUpdateWorkOrderMutation()
  const [isActionDialogOpen, setIsActionDialogOpen] = useState(false)
  const [getAllWorkOrderNotes, { isFetching: isGetAllNotesLoading, data: notesData }] = useLazyGetAllWorkOrderNotesQuery()
  const sortedNotes = getSortedNotes({ notes: get(notesData, 'notes', []) })
  const { archivedNotes } = getArchivedAndUnarchivedNotes({ notes: sortedNotes })
  const [isOpenViewArchiveNoteDialog, setIsOpenViewArchiveNoteDialog] = useState(false)
  const [isOpenAddNoteDialog, setIsOpenAddNoteDialog] = useState(false)
  const {
    workOrderId,
    configuredServiceId,
    routeId,
    invoiceStatus,
    pricedServiceType,
    serviceDate,
    purchaseOrderNumber,
    archivedNotesCount,
    configuredServiceEventType = '',
    sequence,
    serviceLatitude: latitude,
    serviceLongitude: longitude,
    accountName,
  } = workOrder

  const isOneOff = selectIsOneOff({ configuredServiceEventType })
  const canChangeWOInvoiceStatus = canChangeWOInvoiceStatusSelector({ serviceType: pricedServiceType, invoiceStatus })
  const flags = useFlags()
  const isWORedesignEnabled = isWORedesignFeatureAvailable({ flags })

  const handleInvoiceStatusChange = () => {
    updateWorkOrder({
      id: workOrderId,
      invoiceStatus: invoiceStatus === READY_FOR_INVOICE ? NOT_READY_FOR_INVOICE : READY_FOR_INVOICE,
    })
      .unwrap()
      .then(() => {
        onClose()
        toast.success(T.INVOICE_STATUS_UPDATED_SUCCESSFULLY)
        const woInvalidateTags = getWorkOrderListInvalidatesTags({ isNew: isWORedesignEnabled, serviceDate, csId: configuredServiceId })
        dispatch(api.util.invalidateTags(woInvalidateTags))
        onSuccess()
      })
      .catch(handleError)
  }

  const handleOpenAddNoteClick = () => {
    onClose()
    setIsOpenAddNoteDialog(true)
  }

  const handleCloseAddNote = () => {
    setIsOpenAddNoteDialog(false)
  }

  const handleViewArchiveClick = () => {
    onClose()
    setIsOpenViewArchiveNoteDialog(true)
    getAllWorkOrderNotes({ id: workOrderId })
      .unwrap()
      .catch(error => {
        handleError(error)
        setIsOpenViewArchiveNoteDialog(false)
      })
  }

  const handleCloseViewArchiveDialog = () => setIsOpenViewArchiveNoteDialog(false)

  const handleActionDialogOpen = () => {
    setIsActionDialogOpen(true)
  }
  const handleActionDialogClose = () => {
    setIsActionDialogOpen(false)
    onClose()
  }

  const handleOpenAssignDialog = event => {
    event.stopPropagation()
    if (workOrderId) {
      const effectiveServiceDate = serviceDate ? createDateFromString(serviceDate) : new Date()
      dispatch(setRoutingActionServiceDate(effectiveServiceDate))
      const payload = {
        routeId,
        isUnassigned: !routeId,
        addressStr: combineAddressInfo(workOrder),
        id: workOrderId,
        workOrderId,
        latitude,
        longitude,
        accountName,
        toBeInserted: true,
        sequence,
      }
      dispatch(setStopToBeAssigned(payload))
      dispatch(setIsAssignStopDialogOpen(true))
    }
    onClose()
  }

  const handleUnassignStopRouting = event => {
    event.stopPropagation()
    if (workOrderId) {
      const effectiveServiceDate = serviceDate ? createDateFromString(serviceDate) : new Date()
      dispatch(setRoutingActionServiceDate(effectiveServiceDate))
      const payload = {
        routeId,
        isUnassigned: !routeId,
        addressStr: combineAddressInfo(workOrder),
        id: workOrderId,
        workOrderId,
        latitude,
        longitude,
        accountName,
        toBeInserted: !routeId,
        sequence,
      }
      dispatch(setStopToBeAssigned(payload))
      dispatch(setUnassignConfirmDialogAccountId(effectiveAccountId))
      dispatch(setIsUnassignConfirmDialogOpen(true))
    }
    onClose()
  }

  return (
    <>
      {isUpdateWorkOrderLoading && <Loader />}
      <Menu
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        {...(canChangeWOInvoiceStatus && { slotProps: { paper: { sx: { minWidth: 275 } } } })}
      >
        {routeId && workOrderId && (
          <MenuItem
            component={ExternalLinkBehavior}
            to={`${APP_PREFIX}/routes/${routeId}/work_orders/${workOrderId}/export?date=${serviceDate}`}
          >
            <ListItemIcon sx={{ mr: 1 }}>
              <Printer width={24} height={24} />
            </ListItemIcon>
            <ListItemText>{T.PRINTABLE_VIEW}</ListItemText>
          </MenuItem>
        )}
        {canChangeWOInvoiceStatus && (
          <MenuItem onClick={handleInvoiceStatusChange}>
            <ListItemIcon sx={{ mr: 1 }}>
              <CheckCircle width={24} height={24} />
            </ListItemIcon>
            <ListItemText>{INVOICE_STATUS_DISPLAY.READY_FOR_INVOICE.text}</ListItemText>
            <CommonSwitch isChecked={invoiceStatus === READY_FOR_INVOICE} />
          </MenuItem>
        )}
        <Tooltip arrow disableInteractive title={!isOneOff && T.RECURRING_WO_CANNOT_BE_EDITED}>
          <span>
            <MenuItem disabled={!isOneOff} onClick={handleActionDialogOpen}>
              <ListItemIcon sx={{ mr: 1 }}>
                <SvgIcon>
                  <Swap />
                </SvgIcon>
              </ListItemIcon>
              <ListItemText>{T.EDIT_ACTION}</ListItemText>
            </MenuItem>
          </span>
        </Tooltip>
        {enableRoutingActions && (
          <>
            {!routeId && (
              <MenuItem onClick={handleOpenAssignDialog}>
                <ListItemIcon sx={{ mr: 1 }}>
                  <SvgIcon>
                    <AssignStop />
                  </SvgIcon>
                </ListItemIcon>
                {T.ASSIGN_STOP}
              </MenuItem>
            )}
            {routeId && (
              <MenuItem onClick={handleUnassignStopRouting}>
                <ListItemIcon sx={{ mr: 1 }}>
                  <SvgIcon>
                    <AssignStop />
                  </SvgIcon>
                </ListItemIcon>
                {T.UNASSIGN_STOP}
              </MenuItem>
            )}
            {routeId && (
              <MenuItem onClick={handleOpenAssignDialog}>
                <ListItemIcon sx={{ mr: 1 }}>
                  <SvgIcon>
                    <DriverPath />
                  </SvgIcon>
                </ListItemIcon>
                {T.CHANGE_ROUTE_AND_SEQUENCE}
              </MenuItem>
            )}
          </>
        )}
        <MenuItem onClick={handleOpenAddNoteClick}>
          <ListItemIcon sx={{ mr: 1 }}>
            <WorkOrderNoteIcon />
          </ListItemIcon>
          <ListItemText>{T.ADD_NOTE}</ListItemText>
        </MenuItem>
        <ViewArchivedNotesMenuOption isDisabled={archivedNotesCount === 0} count={archivedNotesCount} onClick={handleViewArchiveClick} />
        <AddEditPurchaseOrderMenuOption
          workOrderId={workOrderId}
          serviceDate={serviceDate}
          configuredServiceId={configuredServiceId}
          existingPurchaseOrder={purchaseOrderNumber}
          onClose={onClose}
          onSuccess={onSuccess}
        />
        <RemovePurchaseOrderMenuOption
          workOrderId={workOrderId}
          serviceDate={serviceDate}
          configuredServiceId={configuredServiceId}
          existingPurchaseOrder={purchaseOrderNumber}
          onClose={onClose}
          onSuccess={onSuccess}
        />
      </Menu>
      <AddUpdateNoteDialog
        id={workOrderId}
        isOpen={isOpenAddNoteDialog}
        title={T.NEW_WORK_ORDER_NOTE}
        useCreateNoteMutation={useCreateWorkOrderNoteMutation}
        useUpdateNoteMutation={useUpdateWorkOrderNoteNewMutation}
        onClose={handleCloseAddNote}
      />
      <ViewArchiveNotesDialog
        id={workOrderId}
        isOpen={isOpenViewArchiveNoteDialog}
        isLoading={isGetAllNotesLoading}
        title={T.ARCHIVED_WORK_ORDER_NOTES}
        notes={archivedNotes}
        useUnarchiveNoteMutation={usePatchWorkOrderNoteMutation}
        onClose={handleCloseViewArchiveDialog}
      />
      <EditActionDialog workOrderId={workOrderId} onClose={handleActionDialogClose} open={isActionDialogOpen} />
    </>
  )
}

WorkOrderActionMenu.propTypes = {
  anchorEl: PropTypes.object,
  workOrder: PropTypes.object,
  onClose: PropTypes.func,
  onSuccess: PropTypes.func,
  enableRoutingActions: PropTypes.bool,
}

export default WorkOrderActionMenu
